import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, Delete, X } from 'lucide-react';

interface CustomKeyboardModalProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  placeholder?: string;
  isOpen: boolean;
}

type KeyboardLayout = 'numbers' | 'letters';
const MODAL_ANIMATION_MS = 250;
const LETTER_ROW_GAP_PX = 8;

export const CustomKeyboardModal: React.FC<CustomKeyboardModalProps> = ({
  value,
  onChange,
  onClose,
  isOpen,
}) => {
  const [layout, setLayout] = useState<KeyboardLayout>('numbers');
  const [capsLock, setCapsLock] = useState(true);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setCapsLock(true);
    }
  }, [isOpen]);

  useEffect(() => {
    let timeoutId: number | undefined;

    if (isOpen) {
      setShouldRender(true);
      window.requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      timeoutId = window.setTimeout(() => {
        setShouldRender(false);
      }, MODAL_ANIMATION_MS);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isOpen]);

  if (!shouldRender) {
    return null;
  }

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  const handleKeyPress = (key: string) => {
    const nextKey =
      layout === 'letters' ? (capsLock ? key.toUpperCase() : key.toLowerCase()) : key;

    onChange(value + nextKey);
  };

  const handleDone = () => {
    onClose();
  };

  // Number layout: 3 rows with numbers and common search symbols
  const numberRows = [
    ['1', '2', '3', '-', '.'],
    ['4', '5', '6', '_', '#'],
    ['7', '8', '9', '0', '+'],
  ];

  // Letter layout: QWERTY
  const letterRows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
  ];

  const currentRows = layout === 'numbers' ? numberRows : letterRows;
  const keyClassName =
    layout === 'numbers'
      ? 'flex-1 min-h-[3.5rem] min-w-[3.5rem] max-w-[4.25rem] px-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl font-medium text-gray-900 text-base transition-colors active:scale-95 flex items-center justify-center'
      : 'min-h-[3.5rem] px-2 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl font-medium text-gray-900 text-sm transition-colors active:scale-95 flex items-center justify-center';

  const getLetterKeyStyle = (keyCount: number): React.CSSProperties => {
    const width = `min(3rem, calc((100% - ${(keyCount - 1) * LETTER_ROW_GAP_PX}px) / ${keyCount}))`;

    return {
      flex: `0 1 ${width}`,
      width,
      minWidth: 0,
    };
  };

  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-[100] flex justify-center pointer-events-none">
      <div
        className={`pointer-events-auto w-full responsive-container rounded-t-2xl bg-background shadow-[0_-12px_32px_rgba(15,23,42,0.18)] transition-all transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{
          paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          transitionDuration: `${MODAL_ANIMATION_MS}ms`,
        }}
      >
        <div className="px-4 pt-4 space-y-3">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Key rows */}
          {currentRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={`flex justify-center ${layout === 'letters' ? 'gap-2' : 'gap-3'}`}
            >
              {layout === 'letters' && rowIndex === 2 && (
                <button
                  onClick={() => setCapsLock((current) => !current)}
                  className={`${keyClassName} ${
                    capsLock
                      ? 'bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-950'
                      : ''
                  }`}
                  style={getLetterKeyStyle(row.length + 1)}
                  aria-label={capsLock ? 'Turn caps lock off' : 'Turn caps lock on'}
                >
                  <ChevronUp size={18} />
                </button>
              )}
              {row.map((key) => (
                <button
                  key={key}
                  onClick={() => handleKeyPress(key)}
                  className={keyClassName}
                  style={layout === 'letters' ? getLetterKeyStyle(row.length + (rowIndex === 2 ? 1 : 0)) : undefined}
                >
                  {layout === 'letters' && !capsLock ? key.toLowerCase() : key}
                </button>
              ))}
            </div>
          ))}

          {/* Bottom row: Backspace, Layout toggle, Done */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleBackspace}
              className="flex-1 min-h-[3.5rem] px-5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl font-medium text-gray-900 text-base transition-colors active:scale-95 flex items-center justify-center"
              aria-label="Backspace"
            >
              <Delete size={20} />
            </button>
            <button
              onClick={() =>
                setLayout(layout === 'numbers' ? 'letters' : 'numbers')
              }
              className="flex-1 min-h-[3.5rem] px-5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl font-medium text-gray-900 text-base transition-colors active:scale-95 flex items-center justify-center"
            >
              {layout === 'numbers' ? 'ABC' : '123'}
            </button>
            <button
              onClick={handleDone}
              className="flex-1 min-h-[3.5rem] px-5 hover:bg-opacity-90 active:bg-opacity-100 rounded-xl font-medium text-white text-base transition-colors active:scale-95 flex items-center justify-center"
              style={{ backgroundColor: '#45A17D' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
